import { AccordionItem, Radio, RadioGroup } from "@heroui/react";
import { Accordion } from "@heroui/react";
import { Card } from "@heroui/card";
import { Button } from "@heroui/react";
import { useState, useEffect } from "react";
import {
  IconCreditCard,
  IconBrandPaypal,
  IconDeviceMobile,
  IconMapPin,
  IconBuilding,
} from "@tabler/icons-react";
import useBookingStore, { PaymentMethods } from "@/store/booking-store";
import {
  fetchPaymentSettings,
  parseBankDetails,
  getTwintPhoneNumber,
  getTwintQrCode,
} from "@/lib/paymentApi";
import type { PaymentSettings as PaymentSettingsType } from "@/lib/paymentApi";
import { useTranslations } from "../../../../../i18n";
import { useStudioStore } from "@/store/studio-store";

type PaymentMethod = (typeof PaymentMethods)[keyof typeof PaymentMethods];

function PaymentMethodsSelection() {
  const studio = useStudioStore((state) => state.studio);
  const t = useTranslations("checkout");
  const { setPaymentMethod } = useBookingStore();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(
    null,
  );
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [paymentSettings, setPaymentSettings] = useState<PaymentSettingsType>({
    onlinePayments: false,
    paypalPayments: false,
    payLater: true,
    bankTransfer: false,
    twint: false,
    twintPhoneNumber: null,
    bankDetails: null,
  });

  // Fetch available payment methods from settings
  useEffect(() => {
    const getPaymentSettings = async () => {
      try {
        setIsLoading(true);
        const settings = await fetchPaymentSettings();
        setPaymentSettings(settings);

        // Set initial selected method based on available methods
        if (settings.onlinePayments) {
          setSelectedMethod(PaymentMethods.CARD);
          setExpandedKeys([PaymentMethods.CARD]);
          setPaymentMethod(PaymentMethods.CARD);
        } else if (settings.paypalPayments) {
          setSelectedMethod(PaymentMethods.PAYPAL);
          setExpandedKeys([PaymentMethods.PAYPAL]);
          setPaymentMethod(PaymentMethods.PAYPAL);
        } else if (settings.twint) {
          setSelectedMethod(PaymentMethods.TWINT);
          setExpandedKeys([PaymentMethods.TWINT]);
          setPaymentMethod(PaymentMethods.TWINT);
        } else if (settings.payLater) {
          setSelectedMethod(PaymentMethods.CASH);
          setExpandedKeys([PaymentMethods.CASH]);
          setPaymentMethod(PaymentMethods.CASH);
        } else if (settings.bankTransfer) {
          setSelectedMethod(PaymentMethods.TRANSFER);
          setExpandedKeys([PaymentMethods.TRANSFER]);
          setPaymentMethod(PaymentMethods.TRANSFER);
        }
      } catch (error) {
        console.error("Failed to fetch payment settings:", error);
      } finally {
        setIsLoading(false);
      }
    };

    getPaymentSettings();
  }, [studio]);

  const handlePaymentMethodChange = (value: string) => {
    setSelectedMethod(value as PaymentMethod);
    setExpandedKeys([value]); // Open the accordion for the selected method
    setPaymentMethod(value);
  };

  const handleAccordionChange = (keys: string[]) => {
    setExpandedKeys(keys);
    if (keys.length > 0) {
      setSelectedMethod(keys[0] as PaymentMethod);
      setPaymentMethod(keys[0]);
    }
  };

  // Get bank details from settings
  const bankDetails = parseBankDetails(paymentSettings);

  // Get TWINT phone number and QR code from settings
  const twintPhoneNumber = getTwintPhoneNumber(paymentSettings);
  const twintQrCode = getTwintQrCode(paymentSettings);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[200px] md:min-h-[300px]">
        <div className="text-center">
          <div className="animate-spin h-6 w-6 md:h-8 md:w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-sm md:text-base">
            {t("loadingPaymentOptions") || "Loading payment options..."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div id="payment-options">
      {/* <Card className="block md:hidden p-4 md:p-6 mb-6" shadow="sm">
        <CardBody>
          {selectedEvent && (
            <div className="space-y-3">
              <div>
                <h3 className="text-md font-semibold">
                  {selectedEvent.schedule?.class?.name ||
                    selectedEvent.workshop?.title}
                </h3>
                <div className="flex items-center gap-2 text-gray-500 mt-1">
                  <IconCalendar size={16} />
                  <span className="text-sm">
                    {formatDateWithLocale(
                      new Date(selectedEvent.startTime!),
                      "dd MMM yyyy • HH:mm"
                    )}{" "}
                    -{" "}
                    {formatDateWithLocale(
                      new Date(selectedEvent.endTime!),
                      "HH:mm"
                    )}
                  </span>
                </div>
              </div>


              {discountInfo && (
                <div className="bg-green-50 border border-green-200 rounded-md p-2">
                  <p className="text-green-600 text-xs">
                    {t("discount")}: -{discountInfo.discountAmount}{" "}
                    {studio?.currency || "CHF"}
                  </p>
                </div>
              )}
            </div>
          )}
        </CardBody>
      </Card> */}

      <Card className="p-4 md:p-6">
        <h2 className="text-lg md:text-xl font-semibold text-gray-500 mb-4 md:mb-6">
          {t("selectPaymentMethod")}
        </h2>

        <RadioGroup
          value={selectedMethod || ""}
          onValueChange={handlePaymentMethodChange}
          color="success"
        >
          <Accordion
            selectedKeys={expandedKeys}
            onSelectionChange={(keys) =>
              handleAccordionChange(Array.from(keys as Set<string>))
            }
            className="gap-y-2"
          >
            {/* Online Payments - CARD */}
            {paymentSettings.onlinePayments ? (
              <AccordionItem
                key={PaymentMethods.CARD}
                title={
                  <Radio value={PaymentMethods.CARD} className="w-full">
                    <div className="flex items-center gap-2">
                      <IconCreditCard size={18} className="text-gray-600" />
                      <span className="text-sm md:text-base">
                        {t("payWithCard") || "Pay with credit or debit card"}
                      </span>
                    </div>
                  </Radio>
                }
                className="mb-2"
              >
                <div className="p-3 md:p-4">
                  <p className="text-sm mb-2 text-gray-700">
                    {t("securePaymentWithStripe") ||
                      "Secure payment processed by Stripe"}
                  </p>
                  <div className="flex items-center gap-2 mb-6">
                    {studio?.currency === "CHF" && (
                      <img
                        src="/images/cards/twint.png"
                        alt="Twint"
                        className="h-12 w-auto"
                      />
                    )}
                    <img
                      src="/images/cards/visa.png"
                      alt="Visa"
                      className="h-8 w-auto"
                    />
                    <img
                      src="/images/cards/mastercard.png"
                      alt="Mastercard"
                      className="h-8 w-auto"
                    />
                    <img
                      src="/images/cards/google-pay.png"
                      alt="Google Pay"
                      className="h-8 w-auto"
                    />
                    <img
                      src="/images/cards/apple-pay.png"
                      alt="Apple Pay"
                      className="h-8 w-auto"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-3">
                    {t("redirectToStripe") ||
                      "You'll be redirected to Stripe to complete your payment securely."}
                  </p>
                </div>
              </AccordionItem>
            ) : null}

            {/* TWINT */}
            {paymentSettings.twint ? (
              <AccordionItem
                key={PaymentMethods.TWINT}
                title={
                  <Radio value={PaymentMethods.TWINT} className="w-full">
                    <div className="flex items-center gap-2">
                      <IconDeviceMobile size={18} className="text-gray-600" />
                      <span className="text-sm md:text-base">TWINT</span>
                    </div>
                  </Radio>
                }
                className="mb-2"
              >
                <div className="p-3 md:p-4">
                  <p className="text-sm md:text-base text-gray-600">
                    {t("twintDescription") ||
                      "Please proceed to complete your booking. You can pay using TWINT."}
                  </p>

                  {/* Le QR code n'est PAS affiche ici : a ce stade la
                      reservation n'existe pas encore, et payer avant de
                      reserver laissait des paiements sans contrepartie. Il
                      apparait sur l'ecran suivant, apres "Reserver", avec le
                      bouton de paiement et la confirmation "J'ai paye". */}
                  {twintQrCode && (
                    <p className="mt-3 md:mt-4 text-xs md:text-sm text-gray-500">
                      {t("twintQrInstructions") ||
                        "You'll be able to scan this QR code with your TWINT app once your booking is confirmed."}
                    </p>
                  )}

                  {!twintQrCode && twintPhoneNumber && (
                    <div className="mt-3 md:mt-4 p-3 md:p-4 bg-gray-50 rounded-md">
                      <h3 className="text-sm md:text-base font-medium mb-2">
                        {t("twintPaymentDetails") || "TWINT Payment Details:"}
                      </h3>
                      <p className="text-sm md:text-base">
                        <span className="font-medium">
                          {t("phoneNumber") || "Phone number"}:
                        </span>{" "}
                        {twintPhoneNumber}
                      </p>
                      <p className="text-xs md:text-sm text-gray-500 mt-2">
                        {t("twintInstructions") ||
                          "Use your TWINT app to send payment to this number. Include your booking reference in the payment message."}
                      </p>
                    </div>
                  )}
                </div>
              </AccordionItem>
            ) : null}

            {/* Pay Later - CASH */}
            {paymentSettings.payLater ? (
              <AccordionItem
                key={PaymentMethods.CASH}
                title={
                  <Radio value={PaymentMethods.CASH} className="w-full">
                    <div className="flex items-center gap-2">
                      <IconMapPin size={18} className="text-gray-600" />
                      <span className="text-sm md:text-base">
                        {t("payWhenYouArrive") || "Pay when you arrive"}
                      </span>
                    </div>
                  </Radio>
                }
                className="mb-2"
              >
                <div className="p-3 md:p-4">
                  <p className="text-sm md:text-base text-gray-600">
                    {t("payLaterDescription") ||
                      "Please proceed to complete your booking. You can pay at the studio when you arrive."}
                  </p>
                </div>
              </AccordionItem>
            ) : null}

            {/* Bank Transfer */}
            {paymentSettings.bankTransfer ? (
              <AccordionItem
                key={PaymentMethods.TRANSFER}
                title={
                  <Radio value={PaymentMethods.TRANSFER} className="w-full">
                    <div className="flex items-center gap-2">
                      <IconBuilding size={18} className="text-gray-600" />
                      <span className="text-sm md:text-base">
                        {t("transferToBankAccount") ||
                          "Transfer to bank account"}
                      </span>
                    </div>
                  </Radio>
                }
                className="mb-2"
              >
                <div className="p-3 md:p-4">
                  <p className="text-sm md:text-base text-gray-600">
                    {t("bankTransferDescription") ||
                      "Please proceed to complete your booking, you will receive a confirmation email with the payment details."}
                  </p>

                  {bankDetails && (
                    <div className="mt-3 md:mt-4 p-3 md:p-4 bg-gray-50 rounded-md">
                      <h3 className="text-sm md:text-base font-medium mb-2">
                        {t("bankAccountDetails") || "Bank Account Details:"}
                      </h3>
                      <p className="text-sm md:text-base">
                        <span className="font-medium">
                          {t("accountName") || "Account name"}:
                        </span>{" "}
                        {bankDetails.accountName}
                      </p>
                      <p className="text-sm md:text-base">
                        <span className="font-medium">
                          {t("accountNumber") || "Account number"}:
                        </span>{" "}
                        {bankDetails.accountNumber}
                      </p>
                    </div>
                  )}
                </div>
              </AccordionItem>
            ) : null}

            {/* PayPal */}
            {paymentSettings.paypalPayments ? (
              <AccordionItem
                key={PaymentMethods.PAYPAL}
                title={
                  <Radio value={PaymentMethods.PAYPAL} className="w-full">
                    <div className="flex items-center gap-2">
                      <IconBrandPaypal size={18} className="text-gray-600" />
                      <span className="text-sm md:text-base">
                        {t("payWithPayPal") || "Pay with PayPal"}
                      </span>
                    </div>
                  </Radio>
                }
                className="mb-2"
              >
                <div className="p-3 md:p-4">
                  <Button
                    startContent={<IconBrandPaypal size={18} />}
                    className="w-full"
                    variant="bordered"
                    color="primary"
                    size="sm"
                  >
                    <span className="text-sm md:text-base">
                      {t("continueWithPayPal") || "Continue with PayPal"}
                    </span>
                  </Button>
                </div>
              </AccordionItem>
            ) : null}
          </Accordion>
        </RadioGroup>
      </Card>
    </div>
  );
}

export default PaymentMethodsSelection;
